<script setup lang="ts">
import { computed, ref } from 'vue'
import { CHAT_FLOATING_Z_INDEX } from '@/modules/chat-matrix-fast/constants/ui'
import {
  type MessageWithReply,
  adjustElementPosition,
  getMessageClipboardText,
  useNotifications,
} from '../../chatModules'

interface Props {
  modelValue: boolean
  position: { top: number; left: number } | null
  message: MessageWithReply
  isMine: boolean
  canPin?: boolean
  isPinned?: boolean
  canForward?: boolean
  canReply?: boolean
  canOpenProfile?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'reply'): void
  (e: 'edit'): void
  (e: 'delete'): void
  (e: 'profile'): void
  (e: 'pin'): void
  (e: 'forward'): void
  (e: 'select'): void
}

const props = withDefaults(defineProps<Props>(), {
  canPin: false,
  isPinned: false,
  canForward: false,
  canReply: true,
  canOpenProfile: true,
})
const emit = defineEmits<Emits>()
const { showSuccess, showWarning } = useNotifications()

const menuRef = ref<HTMLElement>()

const adjustedPosition = computed(() => {
  if (!props.position || !menuRef.value) return props.position
  return adjustElementPosition(menuRef.value, props.position)
})

const canShowPin = computed(() =>
  props.canPin && !props.message.is_deleted && Boolean(props.message.matrix_event_id),
)

const canShowForward = computed(() =>
  props.canForward
  && !props.message.is_deleted
  && !props.message.is_system
  && Boolean(props.message.matrix_event_id),
)

const canCopy = computed(() => Boolean(getMessageClipboardText(props.message)))

const handleReply = () => {
  emit('reply')
  emit('update:modelValue', false)
}

const handleEdit = () => {
  emit('edit')
  emit('update:modelValue', false)
}

const handleDelete = () => {
  emit('delete')
  emit('update:modelValue', false)
}

const handleProfile = () => {
  emit('profile')
  emit('update:modelValue', false)
}

const handlePin = () => {
  emit('pin')
  emit('update:modelValue', false)
}

const handleForward = () => {
  emit('forward')
  emit('update:modelValue', false)
}

const handleSelect = () => {
  emit('select')
  emit('update:modelValue', false)
}

const handleCopy = async () => {
  const text = getMessageClipboardText(props.message)
  if (!text) return

  try {
    await navigator.clipboard.writeText(text)
    showSuccess('Сообщение скопировано')
  } catch {
    showWarning('Не удалось скопировать сообщение')
  }

  emit('update:modelValue', false)
}
</script>

<template>
  <Teleport to="body">
    <Transition name="message-menu-fade">
      <div
        v-if="modelValue && position"
        ref="menuRef"
        class="message-menu"
        :style="{
          position: 'fixed',
          top: `${adjustedPosition?.top || position.top}px`,
          left: `${adjustedPosition?.left || position.left}px`,
          transform: 'translateX(-50%)',
          zIndex: CHAT_FLOATING_Z_INDEX,
        }"
      >
        <div class="message-menu-content">
          <button
            v-if="isMine && !message.is_deleted"
            class="message-menu-item"
            type="button"
            @click="handleEdit"
          >
            <VIcon icon="ri-edit-line" size="16" />
            <span>Редактировать</span>
          </button>
          <button
            v-if="canReply"
            class="message-menu-item"
            type="button"
            @click="handleReply"
          >
            <VIcon icon="ri-reply-line" size="16" />
            <span>Ответить</span>
          </button>
          <button
            v-if="canCopy"
            class="message-menu-item"
            type="button"
            @click="handleCopy"
          >
            <VIcon icon="ri-file-copy-line" size="16" />
            <span>Копировать</span>
          </button>
          <button
            v-if="canShowForward"
            class="message-menu-item"
            type="button"
            @click="handleForward"
          >
            <VIcon icon="ri-share-forward-line" size="16" />
            <span>Переслать</span>
          </button>
          <button
            v-if="canShowForward"
            class="message-menu-item"
            type="button"
            @click="handleSelect"
          >
            <VIcon icon="ri-checkbox-multiple-line" size="16" />
            <span>Выбрать</span>
          </button>
          <button
            v-if="canShowPin"
            class="message-menu-item"
            type="button"
            @click="handlePin"
          >
            <VIcon :icon="isPinned ? 'ri-unpin-line' : 'ri-pushpin-2-line'" size="16" />
            <span>{{ isPinned ? 'Открепить' : 'Закрепить' }}</span>
          </button>
          <button
            v-if="canOpenProfile && !isMine"
            class="message-menu-item"
            type="button"
            @click="handleProfile"
          >
            <VIcon icon="ri-user-line" size="16" />
            <span>Профиль</span>
          </button>
          <button
            v-if="isMine && !message.is_deleted"
            class="message-menu-item message-menu-item--danger"
            type="button"
            @click="handleDelete"
          >
            <VIcon icon="ri-delete-bin-line" size="16" />
            <span>Удалить</span>
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.message-menu {
  background: rgb(var(--v-theme-surface));
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 160px;
  overflow: hidden;
}

.message-menu-content {
  display: flex;
  flex-direction: column;
}

.message-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s;
  text-align: left;
  width: 100%;
  color: rgb(var(--v-theme-on-surface));
}

.message-menu-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.message-menu-item--danger {
  color: rgb(var(--v-theme-error));
}

.message-menu-item--danger:hover {
  background-color: rgba(var(--v-theme-error), 0.1);
}

.message-menu-fade-enter-active,
.message-menu-fade-leave-active {
  transition: opacity 0.2s;
}

.message-menu-fade-enter-from,
.message-menu-fade-leave-to {
  opacity: 0;
}
</style>
